import { isExcludedFromDocumentTotal } from "@/lib/documentCalc";
import {
  getPositionMeta,
  linkStrukturTitelLocalIds,
  normalizePositionTyp,
  positionDisplayTitle,
  positionTotals,
  type EditorPosition,
} from "@/pages/dokumente/documentPositionModel";

export interface PositionLayoutRow {
  type: "position";
  index: number;
  displayNumber: string | null;
  indented: boolean;
}

export interface TitelsummeLayoutRow {
  type: "titelsumme";
  titelLocalId: string;
  titelTitle: string;
  sumNetto: number;
}

export type DocumentPositionLayoutItem = PositionLayoutRow | TitelsummeLayoutRow;

function belongsToTitel(row: EditorPosition, titelLocalId: string): boolean {
  return getPositionMeta(row).struktur_titel_local_id === titelLocalId;
}

function isPricedTyp(typ: ReturnType<typeof normalizePositionTyp>): boolean {
  return typ === "artikel" || typ === "leistung";
}

function isLastInTitelGroup(positions: EditorPosition[], index: number, titelLocalId: string): boolean {
  for (let j = index + 1; j < positions.length; j++) {
    const next = positions[j];
    if (next && belongsToTitel(next, titelLocalId)) return false;
  }
  return true;
}

function sumTitelGroupNetto(positions: EditorPosition[], titelLocalId: string): number {
  let sum = 0;
  for (const row of positions) {
    if (!belongsToTitel(row, titelLocalId)) continue;
    const typ = normalizePositionTyp(row.position_typ);
    if (!isPricedTyp(typ)) continue;
    if (isExcludedFromDocumentTotal(row)) continue;
    sum += positionTotals(row).zeilen_netto;
  }
  return Math.round((sum + Number.EPSILON) * 100) / 100;
}

function hasPricedInTitelGroup(positions: EditorPosition[], titelLocalId: string): boolean {
  return positions.some((row) => {
    if (!belongsToTitel(row, titelLocalId)) return false;
    if (isExcludedFromDocumentTotal(row)) return false;
    return isPricedTyp(normalizePositionTyp(row.position_typ));
  });
}

function maybeAppendTitelsumme(
  result: DocumentPositionLayoutItem[],
  positions: EditorPosition[],
  index: number,
  titelLocalId: string,
  titelTitle: string,
): void {
  if (!isLastInTitelGroup(positions, index, titelLocalId)) return;
  if (!hasPricedInTitelGroup(positions, titelLocalId)) return;
  result.push({
    type: "titelsumme",
    titelLocalId,
    titelTitle,
    sumNetto: sumTitelGroupNetto(positions, titelLocalId),
  });
}

/** Positionsnummern, Einrückung und Titelsummen-Zeilen für Dokumentvorschau / PDF. */
export function buildDocumentPositionLayout(positions: EditorPosition[]): DocumentPositionLayoutItem[] {
  const linked = linkStrukturTitelLocalIds(positions);
  const result: DocumentPositionLayoutItem[] = [];
  let topCounter = 0;
  const titelNumberByLocalId = new Map<string, number>();
  const subCounterByTitel = new Map<string, number>();
  const titelTitleByLocalId = new Map<string, string>();

  for (let i = 0; i < linked.length; i++) {
    const row = linked[i];
    if (!row) continue;
    const typ = normalizePositionTyp(row.position_typ);
    const titelLocalId = getPositionMeta(row).struktur_titel_local_id ?? null;

    if (typ === "titel") {
      topCounter += 1;
      titelNumberByLocalId.set(row.localId, topCounter);
      subCounterByTitel.set(row.localId, 0);
      titelTitleByLocalId.set(row.localId, positionDisplayTitle(row));
      result.push({
        type: "position",
        index: i,
        displayNumber: String(topCounter),
        indented: false,
      });
      continue;
    }

    if (typ === "text") {
      const indented = Boolean(titelLocalId && titelNumberByLocalId.has(titelLocalId));
      result.push({ type: "position", index: i, displayNumber: null, indented });
      if (titelLocalId && titelNumberByLocalId.has(titelLocalId)) {
        maybeAppendTitelsumme(
          result,
          linked,
          i,
          titelLocalId,
          titelTitleByLocalId.get(titelLocalId) || "Titel",
        );
      }
      continue;
    }

    if (titelLocalId && titelNumberByLocalId.has(titelLocalId)) {
      const sub = (subCounterByTitel.get(titelLocalId) ?? 0) + 1;
      subCounterByTitel.set(titelLocalId, sub);
      const titelNum = titelNumberByLocalId.get(titelLocalId)!;
      result.push({
        type: "position",
        index: i,
        displayNumber: `${titelNum}.${sub}`,
        indented: true,
      });
      maybeAppendTitelsumme(
        result,
        linked,
        i,
        titelLocalId,
        titelTitleByLocalId.get(titelLocalId) || "Titel",
      );
      continue;
    }

    topCounter += 1;
    result.push({
      type: "position",
      index: i,
      displayNumber: String(topCounter),
      indented: false,
    });
  }

  return result;
}

export function buildPositionDisplayNumbers(positions: EditorPosition[]): (string | null)[] {
  const numbers: (string | null)[] = positions.map(() => null);
  for (const item of buildDocumentPositionLayout(positions)) {
    if (item.type === "position") {
      numbers[item.index] = item.displayNumber;
    }
  }
  return numbers;
}
